// src/views/examples/AdminEditBranch.jsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
} from "reactstrap";
import Header from "components/Headers/Header";
import Swal from "sweetalert2";

import { api } from "../../config/middleware";
import {
  API_GETZIPCODE,
  // ต้องมีใน config/api เช่น "/Branch/GetBranchById"
  API_GETBRANCHBYID,
  // ต้องมีใน config/api เช่น "/Branch/EditBranch"
  API_EDITBRANCH,
} from "../../config/api";

import { useSessionStore } from "../../stores/sessionStore";

const AdminEditBranch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // รับ brId แบบไม่ติด URL:
  // 1) จาก navigate state
  // 2) สำรองเก็บ/อ่านจาก sessionStorage เพื่อรองรับการ refresh
  const stateBrId = location.state?.brId;
  const [brId, setBrId] = useState(() => {
    const s = sessionStorage.getItem("edit_branch_id");
    return stateBrId ?? (s ? Number(s) : undefined);
  });

  useEffect(() => {
    if (stateBrId) sessionStorage.setItem("edit_branch_id", String(stateBrId));
  }, [stateBrId]);

  // โปรไฟล์ผู้ใช้ (ใช้ orgId/tnId เท่าที่จำเป็น)
  const orgId = useSessionStore((s) => s.profile?.orgId) ?? "";
  const tnId = useSessionStore((s) => s.profile?.tnId) ?? "";

  // ฟอร์ม
  const [form, setForm] = useState({
    brCode: "",
    brNameTH: "",
    brNameEN: "",
    cmAddr: "",
    cmAddr2: "",
    postalCode: "",
    subdistrict: "",
    district: "",
    province: "",
    cmMobile: "",
    cmEmail: "",
    customerType: "", // "brBillSameASorg" = ใช้หัวบิลเดียวกัน
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  // ---------- ZIP Search ----------
  const [zipQuery, setZipQuery] = useState("");
  const [zipList, setZipList] = useState([]);
  const [zipOpen, setZipOpen] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const zipBoxRef = useRef(null);

  useEffect(() => {
    if (zipQuery === "") {
      setZipList([]);
      setZipOpen(false);
      return;
    }
    const delay = setTimeout(() => {
      setZipLoading(true);
      const current = zipQuery;
      api
        .get(API_GETZIPCODE, { params: { zipcode: current } })
        .then((res) => {
          if (current !== zipQuery) return;
          const rows = res?.data?.data ?? [];
          setZipList(rows);
          setZipOpen(true);
        })
        .catch(() => {
          if (current !== zipQuery) return;
          setZipList([]);
          setZipOpen(false);
        })
        .finally(() => {
          if (current !== zipQuery) return;
          setZipLoading(false);
        });
    }, 250);
    return () => clearTimeout(delay);
  }, [zipQuery]);

  const onZipChange = (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, postalCode: v }));
    setZipQuery(v);
  };

  const onPickZip = (item) => {
    setForm((s) => ({
      ...s,
      postalCode: String(item.zip_code ?? ""),
      subdistrict: item.subdistrict_name ?? "",
      district: item.district_name ?? "",
      province: item.province_name ?? "",
    }));
    setZipOpen(false);
    setZipList([]);
    setZipQuery("");
  };
  // ---------- END ZIP Search ----------

  // โหลดข้อมูลเดิมของสาขา
  useEffect(() => {
    (async () => {
      if (!brId) {
        await Swal.fire({
          icon: "warning",
          title: "ไม่พบรหัสสาขา",
          text: "กลับไปหน้าเดิมแล้วลองใหม่อีกครั้ง",
        });
        navigate("/branch");
        return;
      }

      try {
        const res = await api.get(API_GETBRANCHBYID, { params: { id: brId } });
        const b = res?.data?.data;
        if (!b) throw new Error("ไม่พบข้อมูลสาขา");

        setForm({
          brCode: b.brCode ?? "",
          brNameTH: b.brNameTH ?? "",
          brNameEN: b.brNameEN ?? "",
          cmAddr: b.brAddr ?? "",
          cmAddr2: b.brAddr2 ?? "",
          postalCode: b.brZipcode ?? "",
          subdistrict: b.brSubDistrict ?? "",
          district: b.brDistrict ?? "",
          province: b.brProvince ?? "",
          cmMobile: b.brMobile ?? "",
          cmEmail: b.brEmail ?? "",
          customerType:
            Number(b.brBillSameASorg) === 1 ? "brBillSameASorg" : "",
        });
      } catch (err) {
        await Swal.fire({
          icon: "error",
          title: "โหลดข้อมูลไม่สำเร็จ",
          text:
            err?.response?.data?.message || err?.message || "เกิดข้อผิดพลาด",
        });
        navigate("/branch");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brId]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    try {
      // payload สำหรับแก้ไข
      const payload = {
        BRID: brId, // <<-- ใส่รหัสสาขาที่จะแก้
        ORGID: orgId,
        TNID: tnId,
        BRCODE: form.brCode,
        BRNAMETH: form.brNameTH,
        BRNAMEEN: form.brNameEN,
        BRADDR: form.cmAddr,
        BRADDR2: form.cmAddr2,
        BRSUBDISTRICT: form.subdistrict,
        BRDISTRICT: form.district,
        BRPROVINCE: form.province,
        BRZIPCODE: form.postalCode,
        BRMOBILE: form.cmMobile,
        BREMAIL: form.cmEmail,
        BRBILLSAMEORG: form.customerType === "brBillSameASorg" ? 1 : 0,
      };

      const res = await api.post(API_EDITBRANCH, payload);
      await Swal.fire({
        icon: "success",
        title: res?.data?.message,
        timer: 1500,
        showConfirmButton: false,
      });

      // ล้าง sessionStorage ที่ใช้เก็บ brId ชั่วคราว
      sessionStorage.removeItem("edit_branch_id");
      navigate("/branch");
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "บันทึกไม่สำเร็จ",
        text: err?.response?.data?.message || err?.message || "เกิดข้อผิดพลาด",
      });
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">แก้ไขข้อมูลสาขา</h6>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form>
                  <h6 className="heading-small text-muted mb-4">
                    ข้อมูลพื้นฐาน
                  </h6>

                  <div className="pl-lg-4">
                    {form.brCode && (
                      <Row>
                        <Col lg="6">
                          <FormGroup className="d-flex align-items-center">
                            <label
                              className="form-control-label mb-0 mr-2"
                              htmlFor="input-customer-code"
                              style={{ minWidth: "20px" }}
                            >
                              รหัสสาขา :
                            </label>
                            <span className="text-dark fw-bold">
                              {form.brCode}
                            </span>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label required">
                            ชื่อสาขา
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="brNameTH"
                            value={form.brNameTH}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            ชื่อสาขา (ภาษาอังกฤษ)
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="brNameEN"
                            value={form.brNameEN}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label required">
                            ที่อยู่
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="cmAddr"
                            value={form.cmAddr}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            ที่อยู่เพิ่มเติม
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="cmAddr2"
                            value={form.cmAddr2}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="3">
                        <FormGroup
                          style={{ position: "relative" }}
                          innerRef={zipBoxRef}
                        >
                          <label className="form-control-label required">
                            รหัสไปรษณีย์
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            value={form.postalCode}
                            onChange={onZipChange}
                            onFocus={() => zipList.length && setZipOpen(true)}
                            autoComplete="off"
                          />

                          {zipOpen && (
                            <div
                              className="dropdown-menu show w-100"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                zIndex: 1050,
                                maxHeight: 240,
                                overflowY: "auto",
                              }}
                            >
                              {zipLoading && (
                                <div className="dropdown-item text-muted small">
                                  กำลังค้นหา…
                                </div>
                              )}
                              {!zipLoading && zipList.length === 0 && (
                                <div className="dropdown-item text-muted small">
                                  ไม่พบข้อมูลรหัสนี้
                                </div>
                              )}
                              {!zipLoading &&
                                zipList.map((it, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="dropdown-item d-flex flex-column align-items-start"
                                    onClick={() => onPickZip(it)}
                                  >
                                    <div>
                                      <strong>{it.zip_code}</strong>
                                    </div>
                                    <div className="small text-muted">
                                      {it.subdistrict_name} • {it.district_name}{" "}
                                      • {it.province_name}
                                    </div>
                                  </button>
                                ))}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label required">
                            ตำบล/แขวง
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            type="text"
                            value={form.subdistrict}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                subdistrict: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label required">
                            อำเภอ/เขต
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            type="text"
                            value={form.district}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                district: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label required">
                            จังหวัด
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            type="text"
                            value={form.province}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                province: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label required">
                            เบอร์โทร
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="cmMobile"
                            value={form.cmMobile}
                            onChange={(e) => {
                              const onlyNums = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              setForm((s) => ({ ...s, cmMobile: onlyNums }));
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={10}
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">Email</label>
                          <Input
                            className="form-control-alternative"
                            type="email"
                            name="cmEmail"
                            value={form.cmEmail}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-3">
                      <Col lg="12">
                        <FormGroup>
                          <Button
                            type="button"
                            outline={form.customerType !== "brBillSameASorg"}
                            color={
                              form.customerType === "brBillSameASorg"
                                ? "primary"
                                : "secondary"
                            }
                            className="mr-2 mb-2 rounded-pill"
                            onClick={() =>
                              setForm((s) => ({
                                ...s,
                                customerType:
                                  s.customerType === "brBillSameASorg"
                                    ? ""
                                    : "brBillSameASorg",
                              }))
                            }
                          >
                            ใช้หัวบิลเดียวกับสำนักงานใหญ่
                          </Button>
                          <Input
                            type="hidden"
                            name="customerType"
                            value={form.customerType}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <ModalFooter>
          <Button color="secondary" onClick={() => navigate("/branch")}>
            ยกเลิก
          </Button>
          <Button color="warning" type="submit" onClick={handleSubmit}>
            อัปเดต
          </Button>
        </ModalFooter>
      </Container>
    </>
  );
};

export default AdminEditBranch;
